import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  CheckCircle, AlertTriangle, XCircle, Circle, Upload, Eye, Replace, RefreshCw,
  FileText, Image, ArrowLeft, ExternalLink, Camera
} from 'lucide-react';
import { openScanModal } from './ScanModal';

const PIECE_LABELS = {
  signe_client: 'Signé par le client',
  complete_par_moi: 'Complété par moi',
  date_livraison_effectuee: 'Livraison effectuée le',
  date_acquittement: 'Facture acquittée le',
  acquittee_par_cheque: 'Acquittée par chèque',
  tamponnee_fournisseur: 'Tamponnée par le fournisseur',
  numeros_serie: 'Numéros de série inscrits',
};

const STATUS_ICONS = {
  ok: { icon: CheckCircle, cls: 'text-emerald-500' },
  manquant: { icon: XCircle, cls: 'text-red-500' },
  manquant_facultatif: { icon: Circle, cls: 'text-gray-300' },
  manquant_recommande: { icon: AlertTriangle, cls: 'text-amber-400' },
  format_invalide: { icon: AlertTriangle, cls: 'text-amber-500' },
  en_attente_validation: { icon: AlertTriangle, cls: 'text-amber-500' },
};

const STATUS_BADGES = {
  ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700' },
  manquant: { label: 'Manquant', cls: 'bg-red-100 text-red-600' },
  manquant_facultatif: { label: 'Non renseigné', cls: 'bg-gray-100 text-gray-500' },
  manquant_recommande: { label: 'Manquant', cls: 'bg-amber-100 text-amber-600' },
  format_invalide: { label: 'Format invalide', cls: 'bg-amber-100 text-amber-700' },
  en_attente_validation: { label: 'À valider', cls: 'bg-amber-100 text-amber-700' },
};

const NIVEAU_LABELS = {
  true: { label: 'OBLIGATOIRE', cls: 'text-red-600 bg-red-50 border-red-200' },
  false: { label: 'RECOMMANDÉ', cls: 'text-amber-600 bg-amber-50 border-amber-200' },
};

export default function CarsatPreparation({ dossierId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  const fetchPreparation = useCallback(async () => {
    try {
      const res = await api.get(`/carsat/${dossierId}/preparation`);
      setData(res.data);
    } catch (e) { toast.error('Erreur chargement préparation'); }
    finally { setLoading(false); }
  }, [dossierId]);

  useEffect(() => { fetchPreparation(); }, [fetchPreparation]);

  // View/download a document with auth
  const viewDocument = async (doc) => {
    if (doc.drive_url || doc.drive_file_url) {
      window.open(doc.drive_url || doc.drive_file_url, '_blank');
      return;
    }
    try {
      const collection = doc.source_collection === 'client_documents' ? 'client-documents' : 'documents';
      const res = await api.get(`/${collection}/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Impossible d\'ouvrir le document');
    }
  };

  useEffect(() => { fetchPreparation(); }, [fetchPreparation]);

  // Save validation (auto-save on each checkbox/date change)
  const saveValidation = async (pieceKey, field, value) => {
    try {
      await api.put(`/carsat/${dossierId}/preparation`, { piece_key: pieceKey, field, value });
      fetchPreparation();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  // Upload or replace document
  const handleUpload = async (pieceKey, typeDoc, replaceDocId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(pieceKey);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type_doc', typeDoc);
        if (replaceDocId) formData.append('replace_doc_id', replaceDocId);
        await api.post(`/carsat/${dossierId}/preparation/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Document uploadé');
        fetchPreparation();
      } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
      finally { setUploading(null); }
    };
    input.click();
  };

  // Upload a File object directly (from scanner)
  const handleScanUpload = async (pieceKey, file) => {
    if (!file) return;
    setUploading(pieceKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type_doc', getDocType(pieceKey));
      await api.post(`/carsat/${dossierId}/preparation/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document scanné uploadé');
      fetchPreparation();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
    finally { setUploading(null); }
  };

  // Get the doc type for upload
  const getDocType = (pieceKey) => {
    const map = { rib: 'rib', attestation_fournisseur: 'attestation_fournisseur', attestation_urssaf: 'urssaf', bon_livraison: 'bon_livraison', minimis: 'de_minimis', facture: 'facture', photos: 'photo_installation' };
    return map[pieceKey] || pieceKey;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw size={24} className="animate-spin text-gray-400" />
    </div>
  );

  if (!data) return <p className="text-center py-8 text-gray-500">Données introuvables</p>;

  const { pieces, progress, statut_global, client_societe } = data;

  const verdictConfig = {
    complet: { label: 'Dossier complet — prêt à envoyer', cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', icon: CheckCircle },
    reserves: { label: 'Dossier prêt avec réserves', cls: 'bg-amber-50 border-amber-200 text-amber-800', icon: AlertTriangle },
    incomplet: { label: 'Dossier incomplet', cls: 'bg-red-50 border-red-200 text-red-800', icon: XCircle },
  };
  const verdict = verdictConfig[statut_global];

  return (
    <div className="space-y-4" data-testid="carsat-preparation">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-testid="prep-back-btn">
          <ArrowLeft size={16} /> Retour
        </button>
        <button onClick={fetchPreparation} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700" data-testid="prep-refresh-btn">
          <RefreshCw size={12} /> Actualiser
        </button>
      </div>

      <div>
        <h2 className="text-lg font-bold text-gray-900">Préparation dossier CARSAT</h2>
        <p className="text-sm text-gray-500">{client_societe}</p>
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="prep-progress">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-medium text-gray-700">{progress.ok}/{progress.total} pièces validées</span>
          <span className="text-gray-500">Obligatoires: {progress.obligatoires_ok}/{progress.obligatoires_total}</span>
        </div>
        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${statut_global === 'complet' ? 'bg-emerald-500' : statut_global === 'reserves' ? 'bg-amber-400' : 'bg-blue-500'}`}
            style={{ width: `${(progress.ok / progress.total) * 100}%` }}
          />
        </div>
      </div>

      {/* Pieces */}
      {pieces.map(piece => (
        <PieceCard
          key={piece.key}
          piece={piece}
          uploading={uploading === piece.key}
          onSaveValidation={saveValidation}
          onUpload={(replaceId) => handleUpload(piece.key, getDocType(piece.key), replaceId)}
          onScan={(file) => handleScanUpload(piece.key, file)}
          onView={viewDocument}
          docType={getDocType(piece.key)}
        />
      ))}

      {/* Verdict */}
      <div className={`rounded-xl border-2 p-5 flex items-center gap-3 ${verdict.cls}`} data-testid="prep-verdict">
        <verdict.icon size={24} className="flex-shrink-0" />
        <div>
          <p className="font-bold text-base">{verdict.label}</p>
          {statut_global === 'incomplet' && (
            <p className="text-sm mt-1 opacity-80">
              {pieces.filter(p => p.obligatoire && p.statut !== 'ok').map(p => p.label).join(', ')} — manquant(s) ou non validé(s)
            </p>
          )}
          {statut_global === 'reserves' && (
            <p className="text-sm mt-1 opacity-80">
              Recommandés manquants : {pieces.filter(p => !p.obligatoire && p.statut !== 'ok').map(p => p.label).join(', ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function PieceCard({ piece, uploading, onSaveValidation, onUpload, onScan, onView, docType }) {
  const { key, label, obligatoire, statut, documents, validations, valeur, pdf_only, source, count, format_ok } = piece;

  // Special rendering for acces_ne piece
  if (key === 'acces_ne') {
    const neStatut = piece.acces_ne_statut;
    const isOk = statut === 'ok';
    const isAnydesk = neStatut === 'anydesk';
    return (
      <div className={`bg-white rounded-xl border p-4 ${isOk ? (isAnydesk ? 'border-amber-200' : 'border-emerald-200') : 'border-red-200'}`} data-testid="piece-acces_ne">
        <div className="flex items-start gap-3">
          {isOk && !isAnydesk ? (
            <CheckCircle size={20} className="mt-0.5 flex-shrink-0 text-emerald-500" />
          ) : isAnydesk ? (
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-amber-500" />
          ) : (
            <XCircle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm">{label}</h3>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-red-600 bg-red-50">OBLIGATOIRE</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isOk ? (isAnydesk ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700') : 'bg-red-100 text-red-600'}`}>
                {isOk ? (isAnydesk ? 'AnyDesk' : 'OK') : 'Bloquant'}
              </span>
            </div>
            <div className="mt-2">
              {neStatut === 'codes_ok' && (
                <p className="text-sm text-emerald-700">Codes NE fonctionnels</p>
              )}
              {neStatut === 'anydesk' && (
                <div>
                  <p className="text-sm text-amber-700 font-medium">AnyDesk prévu avec le comptable</p>
                  <p className="text-xs text-amber-500 mt-0.5">RDV à prendre avec le comptable</p>
                </div>
              )}
              {!neStatut && (
                <p className="text-sm text-red-600 font-medium">Non résolu — bloquant pour la complétion du dossier</p>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">Source : Fiche client</p>
          </div>
        </div>
      </div>
    );
  }

  // Adjust status display based on obligation level
  let displayStatut = statut;
  if (statut === 'manquant' && key === 'medecine_travail') displayStatut = 'manquant_facultatif';
  else if (statut === 'manquant' && !obligatoire) displayStatut = 'manquant_recommande';

  const statusCfg = STATUS_ICONS[displayStatut] || STATUS_ICONS.manquant;
  const StatusIcon = statusCfg.icon;
  const badge = STATUS_BADGES[displayStatut] || STATUS_BADGES.manquant;
  const isMedecineTravail = key === 'medecine_travail';
  const isPhotos = key === 'photos';
  const doc = documents?.[0];

  // Niveau
  let niveauLabel, niveauCls;
  if (obligatoire) {
    niveauLabel = 'OBLIGATOIRE'; niveauCls = 'text-red-600 bg-red-50';
  } else if (key === 'medecine_travail') {
    niveauLabel = 'FACULTATIF'; niveauCls = 'text-gray-500 bg-gray-50';
  } else {
    niveauLabel = 'RECOMMANDÉ'; niveauCls = 'text-amber-600 bg-amber-50';
  }

  return (
    <div className={`bg-white rounded-xl border p-4 ${statut === 'ok' ? 'border-emerald-200' : obligatoire && statut !== 'ok' ? 'border-red-200' : 'border-gray-200'}`} data-testid={`piece-${key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <StatusIcon size={20} className={`mt-0.5 flex-shrink-0 ${statusCfg.cls}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm">{label}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${niveauCls}`}>{niveauLabel}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.cls}`}>{badge.label}</span>
            </div>

            {/* File info */}
            {isMedecineTravail ? (
              <div className="mt-2">
                {valeur ? (
                  <p className="text-sm text-gray-700">{valeur}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Non renseigné dans la fiche client</p>
                )}
                <p className="text-xs text-gray-400 mt-1">Source : Fiche client</p>
              </div>
            ) : isPhotos ? (
              <div className="mt-2">
                {count > 0 ? (
                  <p className="text-sm text-gray-700">{count} photo(s) trouvée(s)</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Aucune photo trouvée</p>
                )}
                <p className="text-xs text-gray-400 mt-1">Source : Documents client</p>
              </div>
            ) : doc ? (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-gray-400" />
                  <span className="text-sm text-gray-700">{doc.filename_original || doc.filename_stored}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                  <span>Source : Documents client</span>
                  {pdf_only && (
                    <span className={format_ok ? 'text-emerald-600' : 'text-red-500'}>
                      Format : {format_ok ? 'PDF ✓' : 'Non PDF ✗'}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-sm text-gray-400 italic">Non trouvé dans les documents client</p>
              </div>
            )}

            {/* Manual validations */}
            {validations && validations.length > 0 && doc && (
              <div className="mt-3 space-y-2 bg-gray-50 rounded-lg p-3" data-testid={`validations-${key}`}>
                <p className="text-xs font-semibold text-gray-500">Vérifications manuelles :</p>
                {validations.map(v => (
                  <ValidationField key={v.key} v={v} pieceKey={key} onSave={onSaveValidation} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {doc && (
            <button onClick={() => onView(doc)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              data-testid={`view-${key}`}>
              <Eye size={12} /> Voir
            </button>
          )}
          {!isMedecineTravail && (
            <div className="flex rounded-lg overflow-hidden border border-gray-200">
              <button onClick={() => onUpload(doc?.id || null)} disabled={uploading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 border-r border-gray-200"
                data-testid={`upload-${key}`}>
                {uploading ? <RefreshCw size={12} className="animate-spin" /> : doc ? <><Replace size={12} /> Remplacer</> : <><Upload size={12} /> Fichier</>}
              </button>
              <button
                onClick={() => openScanModal({ docType: docType, onComplete: (file) => onScan(file) })}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-indigo-600 bg-white hover:bg-indigo-50 transition-colors"
                data-testid={`scan-${key}`}>
                <Camera size={12} /> Scan
              </button>
            </div>
          )}
          {isMedecineTravail && (
            <a href={`/clients/${piece.client_id || ''}`}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              data-testid={`edit-client-${key}`}>
              <ExternalLink size={12} /> Fiche client
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ValidationField({ v, pieceKey, onSave }) {
  const isDate = v.key === 'date_livraison_effectuee' || v.key === 'date_acquittement';
  const label = PIECE_LABELS[v.key] || v.key;

  if (isDate) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!v.value}
          onChange={(e) => {
            if (!e.target.checked) onSave(pieceKey, v.key, null);
          }}
          readOnly={!!v.value}
          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          data-testid={`check-${pieceKey}-${v.key}`}
        />
        <span className="text-sm text-gray-700">{label}</span>
        <input
          type="date"
          value={v.value || ''}
          onChange={(e) => onSave(pieceKey, v.key, e.target.value)}
          className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-400"
          data-testid={`date-${pieceKey}-${v.key}`}
        />
      </div>
    );
  }

  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={!!v.value}
        onChange={(e) => onSave(pieceKey, v.key, e.target.checked)}
        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
        data-testid={`check-${pieceKey}-${v.key}`}
      />
      <span className={`text-sm ${v.ok ? 'text-emerald-700' : 'text-gray-700'}`}>{label}</span>
    </label>
  );
}
